/**
 * Real-time connection health signals (observability RFC). One event per
 * incident: routine closes (token-expiry severs, rotations), retry churn, and
 * offline-machine closes are not captured.
 */
export enum WebSocketAnalyticsEvents {
  /** Cold connect reached open (once per app load); carries durationMs. */
  Connected = 'connected',
  /** A cold connect attempt closed before opening; carries the close code. */
  ConnectFailed = 'connect_failed',
  /**
   * An established connection was lost — the start of an outage. Carries the
   * close code and reason, the socket's lifeMs / framesReceived /
   * pingsReceived / probesSent, and the tab state at close (visibility,
   * hiddenMs, wasDiscarded, tabs, pageTimerLateMs) plus browser hints
   * (brands, brave, standalone, platformVersion). Read with chat-rtm's close
   * line to place a stall (CUS-904).
   */
  UnexpectedClosure = 'unexpected_closure',
  /**
   * Recovered after an outage that took more than one attempt or longer than
   * ROUTINE_RECOVERY_MS; carries code, attempts, durationMs. Routine recoveries
   * are not captured, so this counts slow recoveries, not all of them — take
   * the outage rate from UnexpectedClosure instead.
   */
  Reconnect = 'reconnect',
  /**
   * The socket opened but no server PING arrived within 45 s (one and a half
   * heartbeat ticks). The browser later reports these closes as a bare 1006,
   * so this is the only client signal for a network path that passes the
   * upgrade and then drops frames (CUS-904); carries afterMs, framesReceived.
   */
  SilentSocket = 'silent_socket',
  /**
   * The page thawed after a Page Lifecycle freeze; carries frozenMs, the
   * socket's readyState and lifeMs at that moment, and visibility. Chrome
   * fails a frozen page's WebSocket, so this is the direct test of whether
   * freezing drives the 60 s closes (CUS-904).
   */
  PageResumed = 'page_resumed',
  /**
   * The client clock differs from the token issuer's by more than a minute,
   * measured as receipt time minus the fresh token's iat; once per session.
   * Carries skewMs (positive = client ahead). Without the correction this
   * skew made every fresh token look already-expiring and looped the
   * rotation every few seconds (PE-101).
   */
  ClockSkew = 'clock_skew',
  /**
   * A token that had just been refreshed for a rotation was itself already
   * inside the rotation window, so the proactive rotation was skipped and the
   * server-side expiry sever is left to reconnect; once per session. Carries
   * remainingMs and skewMs (PE-101).
   */
  RotationSkipped = 'rotation_skipped',
}
